import React from 'react';
import {
  CheckCircleIcon,
  XCircleIcon,
  ArrowPathIcon,
  XMarkIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { FileWithPreview, WebUploadResponse } from '../../types/uploads.types';
import { getCategoryIcon, formatFileSize } from './DropZone';

interface UploadProgressProps {
  files: FileWithPreview[];
  isUploading: boolean;
  uploadProgress: number;
  uploadResult?: WebUploadResponse;
  onCancel?: () => void;
  onRetry?: () => void;
  onRemoveFile?: (fileId: string) => void;
  onClear?: () => void;
}

const getStatusIcon = (status: string) => {
  switch (status) {
    case 'accepted':
    case 'completed':
    case 'extracted':
      return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
    case 'rejected':
    case 'failed':
      return <XCircleIcon className="h-5 w-5 text-red-500" />;
    case 'uploading':
    case 'processing':
      return <ArrowPathIcon className="h-5 w-5 text-indigo-500 animate-spin" />;
    default:
      return <div className="h-5 w-5 rounded-full border-2 border-gray-300" />;
  }
};

const getStatusText = (status: string, t: (key: string) => string): string => {
  switch (status) {
    case 'pending':
      return t('uploadProgress.status.pending');
    case 'uploading':
      return t('uploadProgress.status.uploading');
    case 'processing':
      return t('uploadProgress.status.processing');
    case 'accepted':
      return t('uploadProgress.status.accepted');
    case 'rejected':
      return t('uploadProgress.status.rejected');
    case 'extracted':
      return t('uploadProgress.status.extracted');
    case 'completed':
      return t('uploadProgress.status.completed');
    case 'failed':
      return t('uploadProgress.status.failed');
    default:
      return status;
  }
};

const UploadProgress: React.FC<UploadProgressProps> = ({
  files,
  isUploading,
  uploadProgress,
  uploadResult,
  onCancel,
  onRetry,
  onRemoveFile,
  onClear,
}) => {
  const { t } = useTranslation('uploads');

  const hasErrors = (uploadResult?.files_rejected ?? 0) > 0;
  const isComplete = uploadResult && !isUploading;

  return (
    <div className="space-y-4">
      {/* Overall progress bar */}
      {isUploading && (
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">
              {t('uploadProgress.uploading')}
            </span>
            <span className="text-sm text-gray-500">{uploadProgress}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2.5">
            <div
              className="bg-indigo-600 h-2.5 rounded-full transition-all duration-300"
              style={{ width: `${uploadProgress}%` }}
            />
          </div>
          <div className="mt-2 flex justify-between items-center">
            <span className="text-xs text-gray-500">
              {t('uploadProgress.filesCount', { count: files.length })}
            </span>
            {onCancel && (
              <button
                onClick={onCancel}
                className="text-xs text-red-600 hover:text-red-700 font-medium"
              >
                {t('uploadProgress.cancel')}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Completion summary */}
      {isComplete && (
        <div
          className={`rounded-lg border p-4 ${
            hasErrors
              ? 'bg-yellow-50 border-yellow-200'
              : 'bg-green-50 border-green-200'
          }`}
        >
          <div className="flex items-start">
            {hasErrors ? (
              <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500 mt-0.5" />
            ) : (
              <CheckCircleIcon className="h-5 w-5 text-green-500 mt-0.5" />
            )}
            <div className="ml-3 flex-1">
              <h3
                className={`text-sm font-medium ${
                  hasErrors ? 'text-yellow-800' : 'text-green-800'
                }`}
              >
                {hasErrors
                  ? t('uploadProgress.completedWithErrors')
                  : t('uploadProgress.completed')}
              </h3>
              <p className={`mt-1 text-sm ${hasErrors ? 'text-yellow-700' : 'text-green-700'}`}>
                {uploadResult.message}
              </p>
              <div className="mt-2 flex flex-wrap gap-2 text-xs">
                {uploadResult.files_accepted > 0 && (
                  <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-green-800">
                    {t('uploadProgress.filesAccepted', { count: uploadResult.files_accepted })}
                  </span>
                )}
                {uploadResult.files_extracted > 0 && (
                  <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-amber-800">
                    {t('uploadProgress.filesExtracted', { count: uploadResult.files_extracted })}
                  </span>
                )}
                {uploadResult.files_rejected > 0 && (
                  <span className="inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-red-800">
                    {t('uploadProgress.filesRejected', { count: uploadResult.files_rejected })}
                  </span>
                )}
                {uploadResult.total_cdr_records > 0 && (
                  <span className="inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-blue-800">
                    {t('uploadProgress.cdrRecords', { count: uploadResult.total_cdr_records })}
                  </span>
                )}
              </div>
            </div>
            {onClear && (
              <button
                onClick={onClear}
                className="ml-4 text-gray-400 hover:text-gray-500"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            )}
          </div>
          {hasErrors && onRetry && (
            <div className="mt-3 ml-8">
              <button
                onClick={onRetry}
                className="text-sm font-medium text-yellow-700 hover:text-yellow-800"
              >
                {t('uploadProgress.retryFailed')}
              </button>
            </div>
          )}
        </div>
      )}

      {/* File list */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
          <h3 className="text-sm font-medium text-gray-700">
            {t('uploadProgress.fileList')} ({files.length})
          </h3>
        </div>
        <ul className="divide-y divide-gray-200 max-h-80 overflow-y-auto">
          {files.map((fileItem) => {
            const result = uploadResult?.file_results.find(
              (r) => r.filename === fileItem.file.name
            );
            const status = result?.status || fileItem.status;
            const error = result?.error || fileItem.error;

            return (
              <li
                key={fileItem.id}
                className="px-4 py-3 flex items-center justify-between hover:bg-gray-50"
              >
                <div className="flex items-center min-w-0 flex-1">
                  {getCategoryIcon(fileItem.category)}
                  <div className="ml-3 min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {fileItem.file.name}
                    </p>
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <span>{formatFileSize(fileItem.file.size)}</span>
                      {(result?.extracted_files ?? 0) > 0 && (
                        <span className="text-amber-600">
                          ({result?.extracted_files} {t('uploadProgress.extracted')})
                        </span>
                      )}
                      {(result?.cdr_records ?? 0) > 0 && (
                        <span className="text-green-600">
                          ({result?.cdr_records} {t('uploadProgress.records')})
                        </span>
                      )}
                      {error && (
                        <span className="text-red-500 truncate" title={error}>
                          {error}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="ml-4 flex items-center gap-2">
                  <span
                    className={`text-xs px-2 py-0.5 rounded-full ${
                      status === 'accepted' || status === 'completed' || status === 'extracted'
                        ? 'bg-green-100 text-green-700'
                        : status === 'rejected' || status === 'failed'
                        ? 'bg-red-100 text-red-700'
                        : status === 'uploading' || status === 'processing'
                        ? 'bg-indigo-100 text-indigo-700'
                        : 'bg-gray-100 text-gray-700'
                    }`}
                  >
                    {getStatusText(status, t)}
                  </span>
                  {getStatusIcon(status)}
                  {!isUploading && onRemoveFile && status === 'pending' && (
                    <button
                      onClick={() => onRemoveFile(fileItem.id)}
                      className="text-gray-400 hover:text-gray-500"
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
};

export default UploadProgress;
